import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { CONTEXT_METRICS } from "@/api/queries/contextMetrics";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";
import { useLocale } from "@/hooks/useLocale";
import { calculatePercentageChange } from "@/util/statisticsUtils";

export interface ContextMetric {
  value: number;
  previousCycleValue: number;
}

export interface UseContextMetricsReturn {
  metrics: Record<string, ContextMetric>;
  loading: boolean;
  error: unknown;
  getMetricValue: (metricName: string) => number | null;
  getPreviousCycleValue: (metricName: string) => number | null;
  hasMetricData: (metricName: string) => boolean;
  hasPreviousMetricValue: (metricName: string) => boolean;
  getMetricDisplayValue: (
    metricName: string,
    isPercentage?: boolean,
    round?: boolean,
  ) => string;
  getMetricCalculatedPercentChanged: (
    metricName: string,
  ) => number | undefined | null;
}

export const useContextMetrics = (
  orgId?: string,
  context?: OrganizationMetricsContextEnum,
): UseContextMetricsReturn => {
  const { data, loading, error } = useQuery(CONTEXT_METRICS, {
    variables: {
      orgId: orgId || "",
      context:
        context || OrganizationMetricsContextEnum.PredictiveModelsCommitIndex,
    },
    skip: !orgId || !context,
  });

  const locale = useLocale();

  const metrics = useMemo<Record<string, ContextMetric>>(() => {
    if (
      !data?.node ||
      data.node.__typename !== "Organization" ||
      !data.node.metrics
    ) {
      return {};
    }

    const metricsArray = data.node.metrics;
    return metricsArray.reduce(
      (acc, metric) => {
        if (metric?.name) {
          acc[metric.name] = {
            value: metric.value,
            previousCycleValue: metric.previousCycleValue,
          };
        }
        return acc;
      },
      {} as Record<string, ContextMetric>,
    );
  }, [data]);

  const getMetricValue = (metricName: string) => {
    const metric = metrics[metricName];
    if (!metric) return null;
    return metric.value;
  };

  const getPreviousCycleValue = (metricName: string) => {
    const metric = metrics[metricName];
    if (!metric) return null;
    return metric.previousCycleValue;
  };

  const hasMetricData = (metricName: string) => {
    return metrics[metricName] !== null && metrics[metricName] !== undefined;
  };

  const hasPreviousMetricValue = (metricName: string) => {
    return (
      metrics[metricName] !== null &&
      metrics[metricName] !== undefined &&
      metrics[metricName].previousCycleValue !== null &&
      metrics[metricName].previousCycleValue !== undefined
    );
  };

  const getMetricDisplayValue = (
    metricName: string,
    isPercentage = false,
    round = true,
  ) => {
    if (!hasMetricData(metricName)) {
      return "--";
    }
    const value = getMetricValue(metricName);
    if (value === null) {
      return "--";
    }
    const rVal = round ? Math.round(value) : value;
    const val = new Intl.NumberFormat(locale).format(rVal);
    return isPercentage ? `${val}%` : val;
  };

  const getMetricCalculatedPercentChanged = (metricName: string) => {
    const value = getMetricValue(metricName);
    if (value === undefined || value === null) return value;
    const prevValue = getPreviousCycleValue(metricName);
    return calculatePercentageChange(value, prevValue ?? 0);
  };

  return {
    metrics,
    loading,
    error,
    getMetricValue,
    getPreviousCycleValue,
    hasMetricData,
    hasPreviousMetricValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  };
};
